// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { message, Modal } from 'antd';
import PCodesDeltaConfirmation from 'pcodes/components/pcodes-delta-confirmation-modal';
import PCodesDeltaValidationErrorsModal from 'pcodes/components/pcodes-delta-validation-errors-modal';
import prepareHeaders from 'services/utils/prepare-headers';
import labels from 'ui/labels.json';
import ProviderWrapper from 'utils/provider-wrapper';
import { PCode, PCodeHistory, PCodeUploadResult } from './types';

const {
  pcodes: { lbl_noChanges },
} = labels;

type confirmPCodeParams = {
  fileKey: string;
  version: number;
};

// Define a service using a base URL and expected endpoints
export const pcodesApi = createApi({
  reducerPath: 'pcodesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: 'api/pcode',
    prepareHeaders,
  }),
  endpoints: (builder) => ({
    getPCodeOverview: builder.query<Array<PCode>, void>({
      query: () => `getOverview`,
      transformResponse: (rawResult: { data: Array<PCode> }) => rawResult.data,
      keepUnusedDataFor: 0,
    }),
    getPCodeDeltaHistory: builder.query<PCodeHistory<any>, void>({
      query: () => `getDeltaHistory`,
      transformResponse: (rawResult: { data: PCodeHistory<any> }) =>
        rawResult.data,
      keepUnusedDataFor: 0,
    }),
    extractPCodes: builder.mutation<PCodeUploadResult, string>({
      query: (fileKey) => ({
        url: `getPCodeDeltaExtract?fileKey=${fileKey}`,
        method: 'POST',
      }),
      transformResponse: (rawResult: { data: PCodeUploadResult }) =>
        rawResult.data,
      async onQueryStarted(fileKey, { queryFulfilled }) {
        // if errorList is not null --> Validation errors
        try {
          const { data } = await queryFulfilled;
          // Show error Modal
          if (data.errors && data.errors.length > 0) {
            Modal.confirm({
              title: 'Validation Errors',
              wrapClassName: 'pcode-delta-confirm-modal',
              icon: null,
              content: (
                <ProviderWrapper>
                  <PCodesDeltaValidationErrorsModal uploadResult={data} />
                </ProviderWrapper>
              ),
            });
          }
          // Show diff Modal
          else {
            console.log('NO ERROR');
            if (data.pcodes && data.pcodes.length) {
              Modal.confirm({
                title: 'PCode Changes',
                wrapClassName: 'pcode-delta-confirm-modal',
                icon: null,
                content: (
                  <ProviderWrapper>
                    <PCodesDeltaConfirmation
                      fileKey={fileKey}
                      version={data.pcodes[0].version}
                      deltaData={data.pcodes}
                    />
                  </ProviderWrapper>
                ),
              });
            } else {
              message.info(lbl_noChanges);
            }
          }
        } catch (e) {
          console.log(e);
        }
      },
    }),
    confirmPCodeUpload: builder.mutation<Array<PCode>, confirmPCodeParams>({
      query: ({ fileKey, version }) => ({
        url: `confirmPCodeUpload?fileKey=${fileKey}&version=${version}`,
        method: 'POST',
      }),
      transformResponse: (rawResult: { data: Array<PCode> }) => rawResult.data,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        // Update state with new data from response
        const { data } = await queryFulfilled;
        dispatch(
          pcodesApi.util.updateQueryData(
            'getPCodeOverview',
            undefined, // this needs to exactly match the args from the getPCodeOverview or the update will not work
            () => data
          )
        );
      },
    }),
  }),
});

export const {
  useGetPCodeOverviewQuery,
  useGetPCodeDeltaHistoryQuery,
  useExtractPCodesMutation,
  useConfirmPCodeUploadMutation,
} = pcodesApi;
